// ============================================================================
// The sub's latest application, read through from the application record
// (item 833). Nothing about the answers is stored on the sub.
//
// The same lookup ProfileActions already makes to say whether the application
// is back: sourcing_prospects where promoted_provider_id = the sub, newest
// created_at, one row — plus the answers and the language.
//
// WHEN IT WAS SUBMITTED, from what the two submit functions actually write:
//  • A sub's own link (submit_sourcing_intake_for_prospect) stamps the link's
//    `sourcing_intake_tokens.used_at` at the moment of submission. That is the
//    date. (It also touches the application's updated_at, which any later edit
//    moves too, so it is not read.)
//  • The open application link (submit_sourcing_intake) creates the application
//    record in the same call, and makes no link, so with no link at all the
//    record's created_at is the submission.
//  • A link that exists but was never used, or a link the reader may not see:
//    no date is shown rather than a guessed one.
//
// THE FIT SCORE (TCSD-0494, Ben 2026-10-14). The AI function that was built and
// parked (sourcing-ai, TCSD-0401) scores an application that is back and has no
// score yet — or whose score is older than its submission — the first time
// anyone opens the profile. It writes the score and summary onto the
// application itself; everyone after reads the saved one. One AI call per
// application. A failure says so and offers Try again; the answers still show.
//
// Not back yet, no application, or a read the reader is not allowed (row security
// returns no row, not an error) → no application: the profile shows no Application
// section. A read that FAILS is reported as `failed`, so the profile can say so
// rather than look like a sub who never applied.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { refreshReaders, useReader } from '../../../shared/save-path';
import { errorMessage } from '../../../shared/errors';
import { callSourcingAi } from '../../sourcing/hooks/use-sourcing-data';

export interface SubApplication {
  providerId: string;
  prospectId: string;
  intakeJson: unknown;
  language: string | null;
  submittedAt: string | null;
  /** The AI fit score, 0–100, and its short summary — null until it is made. */
  fitScore: number | null;
  fitSummary: string | null;
  fitScoredAt: string | null;
}

export type ScoringState = { kind: 'idle' } | { kind: 'running' } | { kind: 'failed'; message: string };

export interface SubApplicationRead {
  app: SubApplication | null;
  failed: boolean;
  retry: () => void;
  scoring: ScoringState;
  rescore: () => void;
}

/** Why scoring failed, in words an account manager can act on (the function's own codes, mapped). */
export function scoringFailureText(raw: string): string {
  if (/forbidden|403/i.test(raw)) return "you don't have access to scoring applications";
  if (/missing_anthropic_key/i.test(raw)) return "the scoring AI isn't set up — tell Ben";
  if (/llm_parse_failed|502/i.test(raw)) return 'the AI gave an answer the app could not read';
  if (/llm_failed/i.test(raw)) return "the scoring AI didn't answer";
  if (/write_failed/i.test(raw)) return 'the score could not be saved';
  if (/invalid_json|bad_request|400/i.test(raw)) return 'the app sent the scoring AI a request it could not use — tell Ben';
  if (/not signed in|401|missing_or_invalid_auth/i.test(raw)) return 'your sign-in expired — reload the page';
  if (/prospect_not_found|404/i.test(raw)) return 'the application could not be found';
  if (/failed to fetch|network/i.test(raw)) return 'the app could not reach the scoring AI';
  // Anything unmapped stays in the console line the caller writes; never show a code.
  return 'something went wrong on our side';
}

/** True when the application should be (re)scored: no score yet, or scored before it was submitted. */
export function needsScore(a: Pick<SubApplication, 'fitScore' | 'fitScoredAt' | 'submittedAt'>): boolean {
  if (a.fitScore === null) return true;
  return Boolean(a.fitScoredAt && a.submittedAt && a.fitScoredAt < a.submittedAt);
}

// One AI call per application at a time, whoever asks (React mounts twice in
// development, and two cards can open the same sub).
const inFlight = new Set<string>();

export function useSubApplication(providerId: string | undefined): SubApplicationRead {
  const [app, setApp] = useState<SubApplication | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const [run, setRun] = useState<{ pid: string; kind: 'running' | 'failed'; tick: number; message?: string } | null>(null);
  const [scoreTick, setScoreTick] = useState(0);
  const rescore = useCallback(() => setScoreTick((n) => n + 1), []);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useReader(['sourcing_prospects', 'sourcing_intake_tokens'], reload);

  useEffect(() => {
    if (!providerId) return;
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('sourcing_prospects')
        .select('prospect_id, intake_completed, intake_json, intake_language, created_at, fit_score, fit_rationale, fit_scored_at')
        .eq('promoted_provider_id', providerId)
        .order('created_at', { ascending: false })
        .limit(1);
      if (cancelled) return;
      const row = (data ?? [])[0] as
        | {
            prospect_id: string;
            intake_completed: boolean | null;
            intake_json: unknown;
            intake_language: string | null;
            created_at: string;
            fit_score: number | string | null;
            fit_rationale: string | null;
            fit_scored_at: string | null;
          }
        | undefined;
      if (error) {
        setApp(null);
        setFailedFor(providerId);
        return;
      }
      setFailedFor(null);
      if (!row || !row.intake_completed) {
        setApp(null);
        return;
      }
      const links = await supabase.from('sourcing_intake_tokens').select('used_at').eq('prospect_id', row.prospect_id);
      if (cancelled) return;
      let submittedAt: string | null = null;
      if (!links.error) {
        const used = ((links.data ?? []) as Array<{ used_at: string | null }>)
          .map((l) => l.used_at)
          .filter((u): u is string => Boolean(u))
          .sort();
        submittedAt = used.length ? used[used.length - 1] : (links.data ?? []).length === 0 ? row.created_at : null;
      }
      setApp({
        providerId,
        prospectId: row.prospect_id,
        intakeJson: row.intake_json,
        language: row.intake_language,
        submittedAt,
        fitScore: row.fit_score === null || row.fit_score === undefined ? null : Number(row.fit_score),
        fitSummary: row.fit_rationale,
        fitScoredAt: row.fit_scored_at,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [providerId, tick]);

  const shown = app && app.providerId === providerId ? app : null;

  // Score it when it needs one. A failure waits for Try again (scoreTick), so a
  // broken AI is never called in a loop; the state is kept per application.
  const prospectId = shown?.prospectId ?? null;
  const wants = shown ? needsScore(shown) : false;
  useEffect(() => {
    if (!prospectId || !wants || inFlight.has(prospectId)) return;
    if (run && run.pid === prospectId && run.kind === 'failed' && run.tick === scoreTick) return;
    // No cancel on unmount: the call finishes and is saved whoever is looking, and
    // each update only touches this application's own state.
    const pid = prospectId;
    const tick = scoreTick;
    inFlight.add(pid);
    setRun({ pid, kind: 'running', tick });
    void callSourcingAi('fit_score', pid)
      .then(() => {
        setRun((r) => (r && r.pid === pid ? null : r));
        refreshReaders(['sourcing_prospects']);
      })
      .catch((e) => {
        console.warn('[fit score] scoring failed', pid, e);
        setRun((r) => (!r || r.pid === pid ? { pid, kind: 'failed', tick, message: errorMessage(e, 'Could not score the application.') } : r));
      })
      .finally(() => inFlight.delete(pid));
    // `run` is read here, not a trigger: re-running on it would loop.
  }, [prospectId, wants, scoreTick]);

  const scoring: ScoringState =
    run && run.pid === prospectId
      ? run.kind === 'running'
        ? { kind: 'running' }
        : { kind: 'failed', message: run.message ?? 'Could not score the application.' }
      : { kind: 'idle' };

  // Never show one sub's answers on another's page while the next read is in flight.
  return { app: shown, failed: failedFor === providerId, retry: reload, scoring, rescore };
}
